import { computed, ref, watch } from "vue";

import type {
  CustomAttributeDropdownOption,
  SearchSelectProviderOptions,
  UseSearchSelectProviderArgs,
  UseSearchSelectProviderReturn,
} from "@/models/customAttributes/searchSelectDropdown";

export const useSearchSelectProvider: (
  args: UseSearchSelectProviderArgs,
  options?: SearchSelectProviderOptions
) => UseSearchSelectProviderReturn = (
  { needle, items, filterFunction },
  options
) => {
  const callbacks: {
    onHighlighted: Set<(index: number) => void>;
    onSelected: Set<(value: CustomAttributeDropdownOption) => void>;
    onClose: Set<() => void>;
  } = {
    onHighlighted: new Set(),
    onSelected: new Set(),
    onClose: new Set(),
  };

  const sliceTo = options?.slice || undefined;

  const isOpen = ref(false);
  const highlightedIndex = ref(options?.initialHighlightedIndex ?? 0);

  const searchedItems = computed(() =>
    needle.value ? filterFunction(needle.value, items.value) : items.value
  );
  const filteredItems = computed(() =>
    sliceTo ? searchedItems.value.slice(0, sliceTo) : searchedItems.value
  );

  const foundItemsCount = computed(() => searchedItems.value.length);

  const open = () => {
    if (isOpen.value) {
      return;
    }
    isOpen.value = true;
    highlight(0);
  };

  const close = () => {
    if (isOpen.value) {
      isOpen.value = false;
      callbacks.onClose.forEach((fn) => fn());
    }
  };

  const closeAndClear = () => {
    if (!isOpen.value) {
      return;
    }
    highlight(0);
    needle.value = "";
    close();
  };

  const select = (value: (typeof items.value)[number]) => {
    if (isOpen.value) {
      callbacks.onSelected.forEach((fn) => fn(value));
    }
  };

  const selectHighlighted = () => {
    select(filteredItems.value[highlightedIndex.value]);
  };

  const scrollToHighlighted = () => {
    if (options?.scrollRef?.value?.children) {
      options.scrollRef.value.children[highlightedIndex.value].scrollIntoView({
        block: "nearest",
      });
    }
  };

  const highlight = (position: number) => {
    if (position < 0) {
      highlightedIndex.value = filteredItems.value.length - 1;
    } else if (position > filteredItems.value.length - 1) {
      highlightedIndex.value = 0;
    } else {
      highlightedIndex.value = position;
    }
    scrollToHighlighted();
    callbacks.onHighlighted.forEach((fn) => fn(highlightedIndex.value));
  };

  const highlightPrevious = () => {
    highlight(highlightedIndex.value - 1);
  };

  const highlightNext = () => {
    highlight(highlightedIndex.value + 1);
  };

  const onHighlighted = (callback: (index: number) => void) => {
    callbacks.onHighlighted.add(callback);
  };

  const onSelected = (
    callback: (value: (typeof items.value)[number]) => void
  ) => {
    callbacks.onSelected.add(callback);
  };

  const onClose = (callback: () => void) => {
    callbacks.onClose.add(callback);
  };

  watch(filteredItems, () => {
    highlight(0);
  });

  return {
    isOpen,
    highlightedIndex,
    filteredItems,
    foundItemsCount,
    select,
    selectHighlighted,
    open,
    close,
    closeAndClear,
    highlight,
    highlightNext,
    highlightPrevious,
    onHighlighted,
    onSelected,
    onClose,
  };
};
